import React, { useCallback, useMemo, useState } from 'react'
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  CircularProgress,
  Pagination,
  InputAdornment,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Tooltip,
  Switch,
  FormControlLabel
} from '@mui/material'
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material'
import { 
  useGetUsersQuery, 
  useUpdateUserMutation, 
  useSuspendUserMutation, 
  useActivateUserMutation,
  AdminUser,
  UserManagementFilters
} from '@store/api/adminApi'
import { useAppSelector } from '@store/hooks'
import AdminPageGate from '@components/auth/AdminPageGate'
import { ResponsiveTable } from '@components/layout/ResponsiveTable'

const AdminUserManagement: React.FC = () => {
  const { isAuthenticated } = useAppSelector((state) => state.auth)
  const [page, setPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [sortBy, setSortBy] = useState('createdAt')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null)
  const [showUserDialog, setShowUserDialog] = useState(false)
  const [editForm, setEditForm] = useState<Record<string, string | boolean>>({})


  const filters: UserManagementFilters = {
    page,
    limit: 20,
    sortBy,
    sortOrder,
    ...(searchTerm && { search: searchTerm }),
    ...(roleFilter && { role: roleFilter }),
    ...(statusFilter && { isActive: statusFilter === 'active' })
  }

  const { data: usersData, isLoading: usersLoading, error: usersError } = useGetUsersQuery(filters)
  const [updateUser, { isLoading: updateLoading }] = useUpdateUserMutation()
  const [suspendUser, { isLoading: suspendLoading }] = useSuspendUserMutation()
  const [activateUser, { isLoading: activateLoading }] = useActivateUserMutation()

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value)
    setPage(1)
  }

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value)
  }

  const handleEditUser = useCallback((user: AdminUser) => {
    setSelectedUser(user)
    setEditForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      role: user.role || 'user',
      adminNotes: user.adminNotes || '',
      isActive: user.isActive,
    })
    setShowUserDialog(true)
  }, [])

  const handleToggleUserStatus = useCallback(async (user: AdminUser) => {
    try {
      if (user.isActive) {
        await suspendUser(user.id).unwrap()
      } else {
        await activateUser(user.id).unwrap()
      }
    } catch (error) {
      console.error('Failed to toggle user status:', error)
    }
  }, [suspendUser, activateUser])

  const handleUpdateUser = async () => {
    if (!selectedUser) return

    try {
      await updateUser({ id: selectedUser.id, updates: editForm as any }).unwrap()
      setShowUserDialog(false)
      setSelectedUser(null)
      setEditForm({})
    } catch (error) {
      console.error('Failed to update user:', error)
    }
  }

  const clearFilters = () => {
    setSearchTerm('')
    setRoleFilter('')
    setStatusFilter('')
    setPage(1)
  }

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'super_admin': return 'error'
      case 'admin': return 'warning'
      case 'moderator': return 'info'
      default: return 'default'
    }
  }

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'success' : 'error'
  }

  const userColumns = useMemo(
    () => [
      {
        id: 'user',
        header: 'User',
        renderCell: (user: AdminUser) => (
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar>
              {user.firstName?.[0] || user.email[0].toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="subtitle2">
                {user.firstName && user.lastName
                  ? `${user.firstName} ${user.lastName}`
                  : user.username || user.email}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user.email}
              </Typography>
            </Box>
          </Box>
        ),
      },
      {
        id: 'role',
        header: 'Role',
        renderCell: (user: AdminUser) => (
          <Chip label={user.role} color={getRoleColor(user.role) as 'default' | 'error' | 'warning' | 'info'} size="small" />
        ),
      },
      {
        id: 'status',
        header: 'Status',
        renderCell: (user: AdminUser) => (
          <Chip
            label={user.isActive ? 'Active' : 'Inactive'}
            color={getStatusColor(user.isActive) as 'success' | 'error'}
            size="small"
          />
        ),
      },
      {
        id: 'created',
        header: 'Created',
        renderCell: (user: AdminUser) => (
          <Typography variant="body2">{new Date(user.createdAt).toLocaleDateString()}</Typography>
        ),
      },
      {
        id: 'lastLogin',
        header: 'Last Login',
        renderCell: (user: AdminUser) => (
          <Typography variant="body2">
            {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Never'}
          </Typography>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        renderCell: (user: AdminUser) => (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Tooltip title="Edit User">
              <IconButton size="small" onClick={() => handleEditUser(user)}>
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={user.isActive ? 'Suspend User' : 'Activate User'}>
              <IconButton
                size="small"
                onClick={() => handleToggleUserStatus(user)}
                disabled={suspendLoading || activateLoading}
                color={user.isActive ? 'error' : 'success'}
              >
                {user.isActive ? <BlockIcon /> : <CheckCircleIcon />}
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [suspendLoading, activateLoading, handleToggleUserStatus, handleEditUser],
  )

  if (!isAuthenticated) {
    return (
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          User Management
        </Typography>
        <Alert severity="error">
          Please log in to access user management.
        </Alert>
      </Box>
    )
  }

  return (
    <AdminPageGate permission="users:read" title="User Management">
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          User Management
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => window.location.reload()}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedUser(null)
              setShowUserDialog(true)
            }}
          >
            Add User
          </Button>
        </Stack>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search users..."
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                >
                  <MenuItem value="">All Roles</MenuItem>
                  <MenuItem value="user">User</MenuItem>
                  <MenuItem value="moderator">Moderator</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="super_admin">Super Admin</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="createdAt">Created Date</MenuItem>
                  <MenuItem value="lastLoginAt">Last Login</MenuItem>
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="username">Username</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                >
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={clearFilters}
                >
                  Clear
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Users Table */}
      {usersLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : usersError ? (
        <Alert severity="error">
          Failed to load users. Please try again.
        </Alert>
      ) : usersData?.data?.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No users found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your search criteria.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <>
          <ResponsiveTable
            rows={usersData?.data ?? []}
            columns={userColumns}
            getRowId={(user) => user.id}
            expandTableOnMobile
            data-testid="admin-users-list"
          />

          {/* Pagination */}
          {usersData?.meta?.pagination && usersData.meta.pagination.totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={usersData.meta.pagination.totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* User Edit Dialog */}
      <Dialog open={showUserDialog} onClose={() => setShowUserDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                value={selectedUser?.email || ''}
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Username"
                value={selectedUser?.username || ''}
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={editForm.firstName || ''}
                onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={editForm.lastName || ''}
                onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={editForm.role || 'user'}
                  label="Role"
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                >
                  <MenuItem value="user">User</MenuItem>
                  <MenuItem value="moderator">Moderator</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="super_admin">Super Admin</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.isActive as boolean ?? true}
                    onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Admin Notes"
                multiline
                rows={3}
                value={editForm.adminNotes || ''}
                onChange={(e) => setEditForm({ ...editForm, adminNotes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowUserDialog(false); setEditForm({}) }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateUser}
            disabled={updateLoading}
          >
            {updateLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </AdminPageGate>
  )
}

export default AdminUserManagement
